import React, { useMemo, useState } from 'react';

import useFetchClaimStageConfigurations from '~/components/ClaimStages/hooks/useFetchClaimStageConfigurations';
import { CLIVE_GRADIENTS } from '~/components/Clive/cliveRegistry';
import AlertBanner from '~/components/core/AlertBanner';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { ConductorIcon } from '~/components/icons';
import Switch from '~/components/ui/atoms/Switch';
import cn from '~/Utils/cn';

import ActionsCard from '../Actions/ActionsCard';
import copies from '../copies';
import type { ConductorScopeConfigurationModel, ConductorStageRow, ScopeRow, StageDialogFormValues } from '../types';

import GeneralInstructionsCard from './GeneralInstructionsCard';
import GeneralInstructionsDialog from './GeneralInstructionsDialog';
import StageDialog from './StageDialog';
import StagesCard from './StagesCard';

interface ConductorSectionProps {
  organizationId: number;
  /** The (sub-organization, LOB) scope being configured, which the Actions card reads by. */
  scope: ScopeRow;
  configuration: ConductorScopeConfigurationModel;
  /** Applies a sparse patch and resolves to the refreshed scope, or `undefined` on failure. */
  onPatch: (patch: {
    is_enabled?: boolean;
    general_instructions?: string;
    stage?: { key: string; goal: string; instructions: string };
  }) => Promise<unknown>;
}

/** The gradient square the section is badged with, using the existing Clive chip gradient. */
const SectionIcon: React.FC = () => (
  <div className={cn('flex shrink-0 items-center justify-center rounded-lg p-8', CLIVE_GRADIENTS.chip)}>
    <ConductorIcon size={20} iconColor="#305A7F" />
  </div>
);

const ConductorSection: React.FC<ConductorSectionProps> = ({ organizationId, scope, configuration, onPatch }) => {
  const { claimStageConfigurations, isLoading, isError } = useFetchClaimStageConfigurations({ organizationId });
  const [isOpen, setIsOpen] = useState(true);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  // The switch is controlled by the stored value, which only moves once the parent's refetch lands,
  // so without this the control sits on its old position and every further click queues another
  // PATCH for the same scope.
  const [isTogglePending, setIsTogglePending] = useState(false);
  // Keyed on the stage key rather than a list position, which is no longer identity.
  const [editingStageKey, setEditingStageKey] = useState<string | null>(null);

  // Enabled stages only, in claim-stage-configuration order. Order is list position on the
  // wire -- there is no order field -- so this must not sort, and must not derive order from
  // created_at, which does not change when stages are reordered.
  const stages: ConductorStageRow[] = useMemo(
    () =>
      claimStageConfigurations
        .filter((stage) => stage.is_enabled)
        .map((stage) => ({
          key: stage.key,
          label: stage.label,
          content: configuration.stages?.[stage.key],
        })),
    [claimStageConfigurations, configuration.stages]
  );

  const editingStage = stages.find((stage) => stage.key === editingStageKey) ?? null;
  const generalInstructions = configuration.general_instructions ?? '';
  const isEnabled = Boolean(configuration.is_enabled);

  const handleSaveStage = (stageKey: string) => (values: StageDialogFormValues) =>
    onPatch({ stage: { key: stageKey, goal: values.goal, instructions: values.instructions } });

  const handleToggleEnabled = async (checked: boolean) => {
    setIsTogglePending(true);
    try {
      await onPatch({ is_enabled: checked });
    } finally {
      setIsTogglePending(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-12 rounded-xl border-1 border-solid border-blue-200 bg-white p-24">
        <div className="flex items-center gap-16">
          <button
            type="button"
            onClick={() => setIsOpen((wasOpen) => !wasOpen)}
            aria-expanded={isOpen}
            className="flex grow cursor-pointer items-center gap-16 border-0 bg-transparent p-0 text-left"
          >
            <SectionIcon />
            <span className="flex grow flex-col gap-2">
              {/* LG, not XL: the two sections and the sub-cards inside them are only two levels,
                  so an 18px heading over 14px cards left a gap wide enough to read as three. */}
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.BLACK}>
                {copies.editor.conductorTitle}
              </Text>
              <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {isEnabled ? copies.editor.conductorMetaOn(stages.length) : copies.editor.conductorMetaOff}
              </Text>
            </span>
          </button>

          <div className="flex shrink-0 items-center gap-8">
            <Switch
              checked={isEnabled}
              disabled={isTogglePending}
              onChange={(_event, checked: boolean) => handleToggleEnabled(checked)}
              aria-label={copies.editor.conductorSwitchLabel}
            />
            <Text variant={Text.VARIANTS.SM}>
              {isEnabled ? copies.scopeTable.conductorOn : copies.scopeTable.conductorOff}
            </Text>
          </div>
        </div>

        {isOpen ? (
          <div className="flex flex-col gap-16">
            {isEnabled ? null : <AlertBanner alertType="info" note={copies.editor.conductorOffNotice} withIcon />}
            <GeneralInstructionsCard
              generalInstructions={generalInstructions}
              onEdit={() => setIsEditingInstructions(true)}
            />

            {isLoading || isError ? (
              <SkeletonTable rowsCount={4} columnsCount={3} isError={isError} />
            ) : (
              <StagesCard stages={stages} onEditStage={(stage) => setEditingStageKey(stage.key)} />
            )}

            <ActionsCard organizationId={organizationId} scope={scope} isConductorEnabled={isEnabled} />
          </div>
        ) : null}
      </div>

      {isEditingInstructions ? (
        <GeneralInstructionsDialog
          generalInstructions={generalInstructions}
          onClose={() => setIsEditingInstructions(false)}
          onSave={(value) => onPatch({ general_instructions: value })}
        />
      ) : null}

      {editingStage ? (
        <StageDialog
          // Remounted per stage so the markdown editor re-seeds its content, which it does
          // on mount only.
          key={editingStage.key}
          stage={editingStage}
          onClose={() => setEditingStageKey(null)}
          onSave={handleSaveStage(editingStage.key)}
        />
      ) : null}
    </>
  );
};

export default ConductorSection;
